import { produce } from 'immer';
import { isBoolean, sortBy } from 'lodash/fp';
import type { TypedReduxActionOutcomes } from 'app/store/storeTypes';
import {
    type SnowflakeConnectionHealthcheck,
    type Healthcheck,
    UPDATE_SNOWFLAKE_CLIENT_STATUS,
    UPDATE_WAREHOUSE_HEALTH,
    UPDATE_STATUS,
    TOGGLE_WAREHOUSE_LOAD_DIALOG
} from './types';

type WarehouseStatusEntry = {
    name: string;
    size: string;
    state: string;
    samples: Array<{ date: Date; queued: number; running: number }>;
    isOverloaded: boolean;
};

export type HealthcheckSliceType = {
    serviceByName: Record<string, Healthcheck>;
    warehouseStatusByName: Record<string, WarehouseStatusEntry>;
    snowflakeConnection: SnowflakeConnectionHealthcheck | null;
    loadDialog: {
        isOpen: boolean;
        wasOpenedAutomatically: boolean;
        lastClosedAt: Date;
    };
};

export const INITIAL_STATE: HealthcheckSliceType = {
    serviceByName: {},
    warehouseStatusByName: {},
    snowflakeConnection: null,
    loadDialog: {
        isOpen: false,
        wasOpenedAutomatically: false,
        lastClosedAt: new Date()
    }
};

function isOlderThanOneMinute(date: Date): boolean {
    const now = new Date().getTime();
    const then = date.getTime();

    return now - then > 60 * 1000;
}

export const healthcheckReducer = produce((draft, action: TypedReduxActionOutcomes) => {
    switch (action.type) {
        case UPDATE_STATUS: {
            const { name } = action.payload;
            draft.serviceByName[name] = action.payload;
            break;
        }

        case UPDATE_WAREHOUSE_HEALTH: {
            const entries = sortBy('fetchedAt', action.payload.entries);

            // Reset it (warehouses may have changed)
            draft.warehouseStatusByName = {};

            // Initialize each warehouse entry with it's _most recent_ size and state
            entries.at(-1)?.warehouses.forEach(({ name, size, state }) => {
                draft.warehouseStatusByName[name] = {
                    name,
                    size,
                    state,
                    samples: [],
                    isOverloaded: false
                };
            });

            // Populate the samples for each warehouse
            for (const { fetchedAt, warehouses } of entries) {
                for (const { name, queued, running } of warehouses) {
                    draft.warehouseStatusByName[name]?.samples.push({
                        date: new Date(fetchedAt),
                        queued,
                        running
                    });
                }
            }

            // Determine the overload states
            for (const name in draft.warehouseStatusByName) {
                const whs = draft.warehouseStatusByName[name]!;
                whs.isOverloaded = whs.samples.every(({ queued }) => queued > 0);
            }

            // Toggle open the load dialog if necessary
            const selectedWarehouse = draft.snowflakeConnection?.warehouse;
            const { isOpen, lastClosedAt } = draft.loadDialog;

            if (selectedWarehouse && !isOpen) {
                const wh = draft.warehouseStatusByName[selectedWarehouse];
                if (wh?.isOverloaded && isOlderThanOneMinute(lastClosedAt)) {
                    draft.loadDialog.isOpen = true;
                    draft.loadDialog.wasOpenedAutomatically = true;
                }
            }

            break;
        }

        case UPDATE_SNOWFLAKE_CLIENT_STATUS:
            draft.snowflakeConnection = action.payload;
            break;

        case TOGGLE_WAREHOUSE_LOAD_DIALOG: {
            const param = action.payload.isOpen;
            const isOpen = isBoolean(param) ? param : !draft.loadDialog.isOpen;
            draft.loadDialog.isOpen = isOpen;
            draft.loadDialog.wasOpenedAutomatically = false;

            if (!isOpen) {
                draft.loadDialog.lastClosedAt = new Date();
            }

            break;
        }

        default:
            break;
    }
}, INITIAL_STATE);
